/* eslint-disable max-len */
import 'keen-slider/keen-slider.min.css';
import { TrackSlidesConfigOption } from 'keen-slider/react';
import { darken, desaturate } from 'polished';
import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { uuid } from '../../CodesThatsGottaGoSomewhereElse';
import { Icon } from '../../concepts/resources/Icon';
import { Colors, remInPx } from '../../ui';
import { CreateBoardParams, useOnCreateBoard } from '../actions/BoardActions';
import { Flexer } from '../dashboard/elements/LayoutElements';
import { KeenSlider } from '../dashboard/home/KeenSlider';
import { useOnCreateBoardFromTemplate, useTemplateData } from '../dashboard/templates/TemplateHooks';
import { Wibble } from './Wibble';

const CoolButtonLabel = styled.div<{ color: string }>`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: .25rem;
    font-size: 0.9375rem;
    font-weight: 500;
    color: ${p => desaturate(.1, p.color)};
`;

const CoolButtonHero = styled.div<{ color: string }>`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 7.5rem;
    border-radius: .75rem;
    overflow: hidden;
    background: ${p => desaturate(.1, p.color)};

    & > svg {
        color: white;
        width: 1rem;
        height: 1rem;
        transform: scale(3);
        transition: transform .12s ease-out;
    }

    & > img {
        display: block;
        width: 10.75rem;
        height: 7rem;
        border-radius: .5rem;
    }

    &:hover {
        background: ${p => darken(.05, desaturate(.1, p.color))};
        & > svg {
            transform: scale(3.5);
        }
    }

    &:active {
        background: ${p => darken(.1, desaturate(.1, p.color))};
        & > svg {
            transform: scale(3);
        }
    }
`;

const CoolButtonFrame = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: .5rem;
    cursor: pointer;
`;

type CoolButtonProps = {
    color?: string,
    image: string,
    label: string,
    onClick: () => void,
};

const CoolButton = React.memo<CoolButtonProps>(({ color, image, label, onClick }) =>
    <CoolButtonFrame onClick={onClick}>
        <CoolButtonHero color={!!color ? color : Colors.borderLight} >
            <Icon src={image} />
        </CoolButtonHero>
        <CoolButtonLabel color={!!color ? color : Colors.textLight} >
            <Icon src="deqo://icons/hero/plus" size="1" />
            {label}
        </CoolButtonLabel>
    </CoolButtonFrame>
);

const opt = (kind: 'hero' | 'template', label: string, color: string, icon: string, param: string) =>
    ({ id: uuid(), kind, label, color, icon, param });

export const useCreateStripConfigurator = (itemWidth = 11.25, spacing = 1) => {
    const itemWidthPx = useMemo(() => remInPx(itemWidth), [itemWidth]);
    const wibbleWidthPx = useMemo(() => remInPx(.5), []);
    const spacingPx = useMemo(() => remInPx(spacing), [spacing]);
    return useCallback((size: number, slides: HTMLElement[]): TrackSlidesConfigOption => {
        return slides.map((x, i) => ({
            origin: 0,
            size: (i === 4 ? wibbleWidthPx : itemWidthPx) / size,
            spacing: spacingPx / size,
        }));
    }, []);
};

export type NewCreateStripProps = {
    forcePromptForLocation?: boolean,
};

const useOnCreateBoardFromTag = () => {
    const onCreateBoard = useOnCreateBoard();
    return useCallback(({ tag, ...rest }: Omit<CreateBoardParams, 'source'> & { tag: string }) => {
        onCreateBoard({ source: ['tag', tag], autoOpen: true, ...rest });
    }, [onCreateBoard]);
};

export const NewCreateStrip = React.memo((props: NewCreateStripProps) => {

    const onCreateBoardFromTag = useOnCreateBoardFromTag();
    const onCreateBoardFromTemplate = useOnCreateBoardFromTemplate();
    const createOpts = { autoOpen: true, ...props };

    const onConfigure = useCreateStripConfigurator();
    const { templates } = useTemplateData();


    const boardOptions = useMemo(() => {
        const starred = templates?.filter(x => !!x.starred) || [];
        const featured = templates?.filter(x => !!x.metadata.featured) || [];
        const both = [...starred, ...featured];
        return both.map(x => opt('template', x.label, '', x.thumbUrl, x.id));
    }, [templates]);

    return <KeenSlider slides={onConfigure}>
        <KeenSlider.Item>
            <CoolButton
                color="#7E48F0"
                label="Retrospective"
                image="deqo://icons/dash/hero-retro"
                onClick={() => onCreateBoardFromTag({ tag: 'Retrospectives', ...createOpts })}
            />
        </KeenSlider.Item>
        <KeenSlider.Item>
            <CoolButton
                color="#F07CAD"
                label="Estimation"
                image="deqo://icons/dash/hero-estimation"
                onClick={() => onCreateBoardFromTag({ tag: 'Estimation', ...createOpts })}
            />
        </KeenSlider.Item>
        <KeenSlider.Item>
            <CoolButton
                color="#F38C6C"
                label="Planning"
                image="deqo://icons/dash/hero-product"
                onClick={() => onCreateBoardFromTag({ tag: 'Planning', ...createOpts })}
            />
        </KeenSlider.Item>
        <KeenSlider.Item>
            <CoolButton
                color="#F6B263"
                label="Workshop"
                image="deqo://icons/dash/hero-brainstorm"
                onClick={() => onCreateBoardFromTag({ tag: 'MeetingsAndWorkshops', ...createOpts })}
            />
        </KeenSlider.Item>
        <KeenSlider.Item>
            <Flexer flex center height="7.5">
                <Wibble height="2.875" />
            </Flexer>
        </KeenSlider.Item>
        <KeenSlider.Item>
            <CoolButton
                color="#488BF0"
                label="Blank Board"
                image="deqo://icons/dash/add-board"
                onClick={() => onCreateBoardFromTemplate({ templateId: 'BLANK', ...createOpts })}
            />
        </KeenSlider.Item>
        {boardOptions.map(x =>
            <KeenSlider.Item key={x.id}>
                <CoolButtonFrame onClick={() => onCreateBoardFromTemplate({ templateId: x.param, ...createOpts })}>
                    <CoolButtonHero color={Colors.borderLight} >
                        <Icon src={x.icon} />
                    </CoolButtonHero>
                    <CoolButtonLabel color={Colors.textLight} >
                        <Icon src="deqo://icons/hero/plus" size="1" />
                        {x.label.substring(0, 15)}
                    </CoolButtonLabel>
                </CoolButtonFrame>
            </KeenSlider.Item>
        )}
    </KeenSlider>;
});


